import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Checkbox, FormControlLabel, Radio, Stack, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OutlinedInputWithController } from '~/components/reuseable/OutlinedInputWithController';
import { useMFSurveyFormContext } from '~/microfrontend/hooks/useMFSurveyFormContext';
import { SurveyHeaderText } from '~/widgets/survey/components/SurveyHeaderText';
import { HappyScaleResult } from '~/widgets/survey/components/ui/HappyScaleResult';
import { useRootStore } from '~base/hooks/useRootStore';
import { DataShower } from '~base/ui/DataShower';
import { ConfirmModal } from '~components/reuseable/ConfirmModal';
import { SurveyResultCommentForm, SurveyResultCommentFormField } from '~modules/surveys/forms/SurveyResultCommentForm';
import { SurveyResultItemModel } from '~modules/surveys/models/SurveyResultItemModel';
import { surveyCommentsValidationSchema } from '~modules/surveys/schema/SurveyCommentsValidationSchema';
import { SurveyAnswerTypeIdEnum } from '~modules/surveys/types/SurveyAnswerTypeIdEnum';

export const SurveyResultsWidget: React.FC = observer(() => {
  const { surveyStore } = useRootStore();

  const [confirmCloseModalOpen, setConfirmCloseModalOpen] = useState(false);

  const { carId, workOrderId, cardHeader, onSave, disableResults = false } = useMFSurveyFormContext();

  const { t } = useTranslation('survey');

  const methods = useForm<SurveyResultCommentForm>({
    mode: 'onSubmit',
    defaultValues: { ...SurveyResultCommentForm.create() },
    resolver: yupResolver(surveyCommentsValidationSchema),
  });

  const { reset, control, getValues, handleSubmit, trigger } = methods;

  // Effects

  useEffect(() => {
    surveyStore.getSurveyResults({ carId, workOrderId });

    return () => {
      surveyStore.reset();
    };
  }, []);

  useEffect(() => {
    if (!surveyStore.surveyResults) {
      return;
    }

    const preparedValues = surveyStore.prepareSurveyResultCommentFormValues(surveyStore.surveyResults);
    console.log({ ...preparedValues });
    // Автозаполнение формы
    reset({ ...preparedValues });
  }, [surveyStore.surveyResults]);

  // Handlers

  const handleRepeat = () => {
    surveyStore.getSurveyResults({ carId, workOrderId });
  };

  const handleCloseSurvey = async () => {
    // Триггерим валидацию формы, так как тут отправка идет без handleSubmit
    const isValid = await trigger();

    if (!isValid) {
      return;
    }

    void surveyStore.closeSurvey(getValues(), { carId, workOrderId }, () => {
      onSave?.();
      handleCloseConfirmCloseSurveyModal();
    });
  };

  const handleSaveSurvey = () => {
    void surveyStore.saveCommentsFromDealer(getValues(), { carId, workOrderId }, handleRepeat);
  };

  const handleOpenConfirmCloseSurveyModal = (values: SurveyResultCommentForm) => {
    console.log('values', values);
    setConfirmCloseModalOpen(true);
  };

  const handleCloseConfirmCloseSurveyModal = () => {
    setConfirmCloseModalOpen(false);
  };

  // Renders

  const renderAnsweredQuestion = (question: SurveyResultItemModel) => {
    switch (question.answerTypeId) {
      case SurveyAnswerTypeIdEnum.RADIO_GROUP:
        return question.answers.map(({ answerId, descr, textValue }) => {
          return (
            <div key={answerId}>
              <FormControlLabel label={descr} control={<Radio checked disabled />} />
              {textValue && (
                <TextField
                  variant="outlined"
                  value={textValue}
                  disabled
                  fullWidth
                  multiline
                  rows={2}
                  label={t('input.answer.label')}
                />
              )}
            </div>
          );
        });

      case SurveyAnswerTypeIdEnum.CHECKBOX_GROUP:
        return question.answers.map(({ answerId, descr, textValue }) => {
          return (
            <div key={answerId}>
              <FormControlLabel label={descr} control={<Checkbox checked disabled />} />
              {textValue && (
                <TextField
                  variant="outlined"
                  value={textValue}
                  disabled
                  fullWidth
                  multiline
                  rows={2}
                  label={t('input.answer.label')}
                />
              )}
            </div>
          );
        });

      case SurveyAnswerTypeIdEnum.TEXT_SHORT:
        return (
          <TextField variant="outlined" value={question.textValue} disabled fullWidth label={t('input.answer.label')} />
        );

      case SurveyAnswerTypeIdEnum.TEXT_LONG:
        return (
          <TextField
            variant="outlined"
            value={question.textValue}
            disabled
            fullWidth
            multiline
            rows={4}
            label={t('input.answer.label')}
          />
        );

      case SurveyAnswerTypeIdEnum.HAPPY_INDEX_5:
      case SurveyAnswerTypeIdEnum.HAPPY_INDEX_10:
        return (
          <HappyScaleResult
            length={question.answerTypeId === SurveyAnswerTypeIdEnum.HAPPY_INDEX_5 ? 5 : 10}
            selectedValue={question.happyIndex}
            scaleType={question.answerTypeId}
          />
        );

      default:
        return null;
    }
  };

  return (
    <DataShower
      loading={surveyStore.surveyResultsLoading}
      success={surveyStore.surveyResultsLoaded}
      repeatAction={handleRepeat}
    >
      {cardHeader}
      <SurveyHeaderText headerText={surveyStore.surveyResults?.headerText} />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleOpenConfirmCloseSurveyModal)}>
          <Stack direction="column" gap={3} mb={3}>
            {surveyStore.surveyResults?.results.map((question, index) => (
              <Box key={question.questionId}>
                <Typography variant="subtitle2" mb={2}>
                  {`${index + 1}. ${question.descr ?? ''}`}
                </Typography>
                {renderAnsweredQuestion(question)}

                {question.isNeedComment && (
                  <Box mt={2}>
                    <OutlinedInputWithController
                      name={`${SurveyResultCommentFormField.comments}.${question?.questionId ?? ''}`}
                      hookFormProps={{ control }}
                      size="small"
                      label={t('input.comment.label')}
                      fullWidth
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Stack>

          {!disableResults && (
            <Stack direction="row" justifyContent="space-between">
              <Button type="submit" variant="contained">
                {t('button.close', { ns: 'common' })}
              </Button>
              <Button variant="contained" color="success" onClick={handleSaveSurvey}>
                {t('button.save', { ns: 'common' })}
              </Button>
            </Stack>
          )}
        </form>
      </FormProvider>

      <ConfirmModal
        open={confirmCloseModalOpen}
        title={t('modal.confirmClose.title')}
        titleProps={{ color: 'error' }}
        desc={t('modal.confirmClose.descr')}
        onDisagree={handleCloseConfirmCloseSurveyModal}
        onAgree={handleCloseSurvey}
      />
    </DataShower>
  );
});
